import React, { useEffect } from "react";
import { useArrayContext } from "../../../state/ArrayContext";
import { useSiteContext } from "../../../state/SiteContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { useStoredItem } from "../../../state/InternalContext";
import { getValidityPathLabel } from "../../../shared/utils/validity.utils";

interface ValidityMatrixProps {
    item: string,
}

/***
 * Array source that returns the the validity matrix of a stored item.
 * Each row in the matrix is represented in the output as a SourceItem with a children property.
 * The children are the validity values for the row.
 * 
 * Each value in the row is marked with the metadata definition [info.name] which
 * is the name of the validity property, from the site definition.
 * Values which are the same as in the previous row are marked with the metadata
 * definition [info.isrepeat=true].
*/
export const ValidityMatrix = React.memo(function ValidityMatrix({ item }: ValidityMatrixProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const { siteState } = useSiteContext();
    const storedItem = useStoredItem(item);

    useEffect(() => {
        const result: Array<SourceItem> = [];

        const validity: string[] = storedItem?.validity || [];

        const nonEmptyPaths: string[] = validity.filter((value) => value.trim() !== "");

        // Filter out rows that are included as prefix of others
        const filteredValidity: string[] =
            nonEmptyPaths.filter((value, index) => !nonEmptyPaths.slice(index + 1).some(row => row.startsWith(value + ":")));

        // Build an array of all validity values in the validity tree.
        const previousRow: Array<string> = [];
        for (let path of filteredValidity) {
            const childItems: Array<SourceItem> = [];
            const pathArray: Array<string> = path.split(":");
            let accumulatedPath: string[] = [];
            for (let segment of pathArray) {
                if (!segment.startsWith("$")) { // Skip preview validities
                    accumulatedPath.push(segment);
                    childItems.push({
                        value: segment,
                        props: {
                            info: {
                                "name": [getValidityPathLabel(accumulatedPath, siteState.site?.validities || null)],
                                "isrepeat": [segment === previousRow[accumulatedPath.length - 1]],
                            }
                        },
                        position: accumulatedPath.length,
                    });
                }
            }
            if (childItems.length === 0 || childItems.every((child) => child.value === "")) {
                continue;
            }
            previousRow.length = 0;
            previousRow.push(...pathArray);
            result.push({
                value: "",
                props: {},
                children: childItems,
            });
        }
        setArray(result);
    }, [storedItem, siteState, setArray]);

    return <></>;
});